import { message } from "antd";

import {
    getPlansFileByPageIdGetV2QueryKey,
    plansAnnotationsByPageIdGetV2,
    plansFileByPageIdGetV2,
} from "@btnet/services-btnet/handlers/plans/plans";

import { type BTLoginTypes } from "types/enum";

import {
    getFileNameFromAxiosResponse,
    triggerBrowserDownload,
} from "utilities/document/fileDownload.utils";
import { reportError } from "utilities/errorHelpers";

import { flattenPlanAnnotations } from "./flattenPlanAnnotations";
import { buildDirectUrlFromQueryKey } from "./plans.utils";

export interface IBuildPlanPageFileUrlArgs {
    pageId: string;
    jobId: number;
    loginType: BTLoginTypes;
    bidId?: number;
    /**
     * Specific GCP generation to target. Omit to get the page's current
     * version. Callers that target a `PlanVersionHistoryItem` pass this; the
     * plan card targets the current version and omits it.
     */
    generationId?: number;
}

export function buildPlanPageFileUrl({
    pageId,
    jobId,
    loginType,
    bidId,
    generationId,
}: IBuildPlanPageFileUrlArgs): string {
    // Only include optional fields when defined. `buildDirectUrlFromQueryKey`
    // feeds the params object straight into URLSearchParams, which serializes
    // `undefined` as the literal string "undefined" — producing
    // `generationId=undefined` in the URL otherwise.
    const queryKey = getPlansFileByPageIdGetV2QueryKey(pageId, {
        jobId,
        ...(generationId !== undefined ? { generationId } : {}),
        ...(bidId !== undefined ? { bidId: Number(bidId) } : {}),
    });
    return buildDirectUrlFromQueryKey(queryKey, loginType);
}

export interface IBuildPlanPageFilenameArgs {
    baseFileName: string;
    annotated: boolean;
    /**
     * When provided, the filename gets a `-v{N}` suffix between the base name
     * and the annotated suffix. Omit when the download targets the current
     * version (e.g. plan card menu) and no version disambiguation is needed.
     */
    versionNumber?: number;
}

export function buildPlanPageFilename({
    baseFileName,
    annotated,
    versionNumber,
}: IBuildPlanPageFilenameArgs): string {
    const baseName = baseFileName.replace(/\.pdf$/i, "");
    const versionPart = versionNumber !== undefined ? `-v${versionNumber}` : "";
    const annotatedPart = annotated ? "-markups" : "";
    return `${baseName}${versionPart}${annotatedPart}.pdf`;
}

export interface IDownloadPlanPagePlainArgs {
    pageId: string;
    jobId: number;
    fallbackFilename: string;
    /** Free-form context for Sentry/internal notes (e.g. "pageId: X, version: 2"). */
    errorContext: string;
    bidId?: number;
    /** Specific GCP generation to download. Omit for the current version. */
    generationId?: number;
}

/**
 * Plain plan-page download: goes through the orval-generated
 * `plansFileByPageIdGetV2` (apixHandler → axios → blob), then hands the blob
 * to the cross-context download helper (desktop + mobile webview).
 * Server-provided Content-Disposition takes precedence over the fallback
 * filename.
 */
export async function downloadPlanPagePlain({
    pageId,
    jobId,
    fallbackFilename,
    errorContext,
    bidId,
    generationId,
}: IDownloadPlanPagePlainArgs): Promise<void> {
    try {
        const response = await plansFileByPageIdGetV2(pageId, {
            jobId,
            ...(generationId !== undefined ? { generationId } : {}),
            ...(bidId !== undefined ? { bidId: Number(bidId) } : {}),
        });
        const filename = getFileNameFromAxiosResponse(response, fallbackFilename);
        triggerBrowserDownload(response.data, filename);
    } catch (error) {
        reportError(error, {
            internalNotes: `Plan download failed for ${errorContext}`,
        });
        void message.error("Failed to download plan. Please try again.");
    }
}

export interface IDownloadPlanPageAnnotatedArgs {
    pageId: string;
    jobId: number;
    isCurrentVersion: boolean;
    /** Nutrient license key from `AppDefaultInfoContext.pspdfkitLicenseKey`. */
    licenseKey: string | undefined;
    /** Pre-built file URL (from `buildPlanPageFileUrl`). */
    fileUrl: string;
    /** Pre-built filename (from `buildPlanPageFilename` with `annotated: true`). */
    filename: string;
    /** Free-form context for Sentry/internal notes. */
    errorContext: string;
    /** Specific GCP generation to fetch annotations for. Omit for current. */
    generationId?: number | null;
}

/**
 * Annotated plan-page download: fetches the stored annotations for the target
 * generation, mounts a hidden secondary Nutrient instance to flatten them
 * into a PDF, then triggers the download via the cross-context helper.
 * Never disturbs the user's active viewer.
 */
export async function downloadPlanPageAnnotated({
    pageId,
    jobId,
    isCurrentVersion,
    licenseKey,
    fileUrl,
    filename,
    errorContext,
    generationId,
}: IDownloadPlanPageAnnotatedArgs): Promise<void> {
    try {
        const annotationsResponse = await plansAnnotationsByPageIdGetV2(pageId, {
            jobId,
            generationId: generationId ?? undefined,
            isCurrentVersion,
        });
        const annotations = annotationsResponse.data || undefined;
        const blob = await flattenPlanAnnotations({
            licenseKey,
            documentUrl: fileUrl,
            instantJSON: annotations?.instantJson ?? undefined,
            xfdf: annotations?.xfdf ?? undefined,
        });
        triggerBrowserDownload(blob, filename);
    } catch (error) {
        reportError(error, {
            internalNotes: `Plan annotated download failed for ${errorContext}`,
        });
        void message.error("Failed to download annotated plan. Please try again.");
    }
}
